// Temporal filter for the board — "how long has this card been here".
//
// We anchor on a card's arrival time (`source_date ?? created_at`), then
// keep only cards whose arrival falls inside the chosen window. Three
// flavours of window, mirroring the way people actually reason about time:
//
//   • rolling   — a sliding window ending NOW ("the last N hours/days").
//                 Best for recency ("what landed since I last looked").
//   • calendar  — from the start of the CURRENT day/week/month up to now
//                 ("aujourd'hui", "cette semaine", "ce mois-ci").
//   • previous  — an ISOLATED past bucket, fully bounded ("seulement hier",
//                 "seulement la semaine dernière"). The key distinction the
//                 user asked for: « depuis hier » (rolling, includes today)
//                 vs « seulement hier » (just that calendar day).
//   • range     — an explicit from/to date range (the period picker).
//
// `all` disables the filter (default).

export type TimeFilter =
  | { kind: 'all' }
  | { kind: 'rolling'; unit: 'hour' | 'day'; count: number }
  // Cumulative windows ending NOW, all « depuis … jusqu'à maintenant »:
  //   today    — since 00:00 today
  //   yesterday— since 00:00 yesterday (includes today)
  //   week     — since the start of the current week (Monday 00:00)
  //   week7    — rolling last 7×24h
  //   month    — since the 1st of the current month
  | {
      kind: 'calendar'
      bucket: 'today' | 'yesterday' | 'week' | 'week7' | 'month'
    }
  | { kind: 'previous'; bucket: 'yesterday' | 'lastweek' | 'lastmonth' }
  | { kind: 'range'; from: string; to: string } // YYYY-MM-DD inclusive

export const ALL_TIME: TimeFilter = { kind: 'all' }

const HOUR_MS = 3_600_000
const DAY_MS = 86_400_000

/** Midnight (local) at the start of the day containing `d`. */
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/** Monday 00:00 (local) of the week containing `d`. ISO week — Monday is
 *  the first day, matching the French/European convention. */
function startOfWeek(d: Date): Date {
  const s = startOfDay(d)
  // getDay(): 0 = Sunday … 6 = Saturday. Map so Monday = 0.
  const dow = (s.getDay() + 6) % 7
  s.setDate(s.getDate() - dow)
  return s
}

/** First day of the month containing `d`, at 00:00 local. */
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

/** Parse a YYYY-MM-DD string as a LOCAL date at 00:00 (not UTC — avoids
 *  the off-by-one that `new Date('2026-01-01')` causes in negative-UTC
 *  zones). Returns null on a malformed string. */
function parseLocalDate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!m) return null
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

/** Does a card that arrived at `anchorIso` fall inside `filter`?
 *  `nowMs` is injected so the same wall-clock is used across a whole
 *  filtering pass (and so it stays testable). */
export function matchesTimeFilter(
  filter: TimeFilter,
  anchorIso: string | null | undefined,
  nowMs: number,
): boolean {
  if (filter.kind === 'all') return true
  if (!anchorIso) return false
  const at = new Date(anchorIso).getTime()
  if (Number.isNaN(at)) return false
  const now = new Date(nowMs)

  switch (filter.kind) {
    case 'rolling': {
      const span = (filter.unit === 'hour' ? HOUR_MS : DAY_MS) *
        Math.max(1, filter.count)
      return at >= nowMs - span && at <= nowMs
    }
    case 'calendar': {
      let from: number
      if (filter.bucket === 'today') from = startOfDay(now).getTime()
      else if (filter.bucket === 'yesterday')
        from = startOfDay(now).getTime() - DAY_MS
      else if (filter.bucket === 'week') from = startOfWeek(now).getTime()
      else if (filter.bucket === 'week7') from = nowMs - 7 * DAY_MS
      else from = startOfMonth(now).getTime() // month
      return at >= from && at <= nowMs
    }
    case 'previous': {
      if (filter.bucket === 'yesterday') {
        const todayStart = startOfDay(now).getTime()
        return at >= todayStart - DAY_MS && at < todayStart
      }
      if (filter.bucket === 'lastweek') {
        const weekStart = startOfWeek(now).getTime()
        const prevStart = startOfWeek(new Date(weekStart - DAY_MS)).getTime()
        return at >= prevStart && at < weekStart
      }
      // lastmonth
      const monthStart = startOfMonth(now).getTime()
      const prevMonthStart = startOfMonth(
        new Date(monthStart - DAY_MS),
      ).getTime()
      return at >= prevMonthStart && at < monthStart
    }
    case 'range': {
      const from = parseLocalDate(filter.from)
      const to = parseLocalDate(filter.to)
      if (!from || !to) return false
      // `to` is inclusive → push to the END of that calendar day.
      const toEnd = to.getTime() + DAY_MS
      const lo = Math.min(from.getTime(), to.getTime())
      const hi = Math.max(from.getTime() + DAY_MS, toEnd)
      return at >= lo && at < hi
    }
    default:
      return true
  }
}

/** True when the filter actually narrows the board (i.e. not « all »). */
export function isTimeFilterActive(filter: TimeFilter): boolean {
  return filter.kind !== 'all'
}
